import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Separator } from '../../components/ui/separator';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useCart } from '../../store/cartContext';
import { orderService } from '../../services/orderService';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';
import { SHIPPING_FEE_RSD } from '@/constants/delivery';
import { DeliveryNotice } from '../../components/shared/DeliveryNotice';

const schema = z.object({
  firstName: z.string().min(2, 'Unesite ime'),
  lastName: z.string().min(2, 'Unesite prezime'),
  phone: z.string().min(9, 'Unesite telefon'),
  email: z.string().email('Unesite ispravnu email adresu'),
  address: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  note: z.string().optional()
});

export default function CheckoutPage() {
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [deliveryType, setDeliveryType] = useState('DELIVERY');
  const [pricing, setPricing] = useState({
    subtotal: totalPrice,
    shipping: SHIPPING_FEE_RSD,
    total: totalPrice + SHIPPING_FEE_RSD
  });

  const { register, handleSubmit, setError, clearErrors, formState: { errors } } = useForm({
    resolver: zodResolver(schema)
  });

  React.useEffect(() => {
    if (items.length === 0) {
      return;
    }
    orderService.previewOrder(items, deliveryType)
      .then((preview) => {
        setPricing({
          subtotal: Number(preview.subtotal || 0),
          shipping: Number(preview.shipping || 0),
          total: Number(preview.total || 0)
        });
      })
      .catch((e) => {
        const localShipping = deliveryType === 'PICKUP' ? 0 : SHIPPING_FEE_RSD;
        setPricing({
          subtotal: totalPrice,
          shipping: localShipping,
          total: totalPrice + localShipping
        });
        toast.error(e.message || 'Greška pri obracunu dostave');
      });
  }, [items, deliveryType, totalPrice]);

  React.useEffect(() => {
    if (deliveryType === 'PICKUP') {
      clearErrors(['address', 'city', 'postalCode']);
    }
  }, [deliveryType, clearErrors]);

  React.useEffect(() => {
    if (!orderPlaced && !submitting && items.length === 0) {
      navigate('/korpa', { replace: true });
    }
  }, [items.length, orderPlaced, submitting, navigate]);

  if (!orderPlaced && items.length === 0) {
    return null;
  }

  const onSubmit = async (data) => {
    if (deliveryType === 'DELIVERY') {
      const address = (data.address || '').trim();
      const city = (data.city || '').trim();
      const postalCode = (data.postalCode || '').trim();

      let hasDeliveryValidationError = false;
      if (address.length < 5) {
        setError('address', { type: 'manual', message: 'Unesite adresu' });
        hasDeliveryValidationError = true;
      }
      if (city.length < 2) {
        setError('city', { type: 'manual', message: 'Unesite grad' });
        hasDeliveryValidationError = true;
      }
      if (postalCode.length < 4) {
        setError('postalCode', { type: 'manual', message: 'Unesite postanski broj' });
        hasDeliveryValidationError = true;
      }

      if (hasDeliveryValidationError) {
        return;
      }
    }

    setSubmitting(true);
    try {
      const orderItems = items.map(i => ({ productId: i.product.id, quantity: i.quantity }));
      const order = await orderService.createOrder({ ...data, deliveryType, items: orderItems });
      setOrderPlaced(true);

      // Google Ads / GTM konverzija - okida se na uspesnu porudzbinu
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'purchase',
        transaction_id: order.orderNumber,
        value: pricing.total,
        currency: 'RSD',
      });

      navigate('/uspesna-porudzbina', { state: { orderNumber: order.orderNumber }, replace: true });
      clearCart();
    } catch (e) {
      toast.error('Doslo je do greske. Pokušajte ponovo.');
    } finally {
      setSubmitting(false);
    }
  };

  const FieldError = ({ name }) => errors[name] ? (
    <p className="text-destructive text-xs font-ui mt-1">{errors[name].message}</p>
  ) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-bold text-foreground">Plaćanje</h1>
        <p className="text-muted-foreground font-ui text-sm mt-1">Unesite podatke za dostavu</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-xl shadow-card border border-border p-6"
            >
              <h2 className="font-serif text-lg font-semibold text-foreground mb-5">Licni podaci</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="font-ui text-sm text-foreground mb-1.5 block">Ime *</Label>
                  <Input id="firstName" {...register('firstName')} placeholder="Marko" className="font-ui" />
                  <FieldError name="firstName" />
                </div>
                <div>
                  <Label htmlFor="lastName" className="font-ui text-sm text-foreground mb-1.5 block">Prezime *</Label>
                  <Input id="lastName" {...register('lastName')} placeholder="Jovic" className="font-ui" />
                  <FieldError name="lastName" />
                </div>
                <div>
                  <Label htmlFor="phone" className="font-ui text-sm text-foreground mb-1.5 block">Telefon *</Label>
                  <Input id="phone" {...register('phone')} placeholder="+381601140402" className="font-ui" />
                  <FieldError name="phone" />
                </div>
                <div>
                  <Label htmlFor="email" className="font-ui text-sm text-foreground mb-1.5 block">Email *</Label>
                  <Input id="email" type="email" {...register('email')} placeholder="marko@gmail.com" className="font-ui" />
                  <FieldError name="email" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card rounded-xl shadow-card border border-border p-6"
            >
              <h2 className="font-serif text-lg font-semibold text-foreground mb-5">Način preuzimanja</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => setDeliveryType('DELIVERY')}
                  className={`rounded-xl border p-4 text-left transition-colors ${
                    deliveryType === 'DELIVERY'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <p className="font-ui text-sm font-semibold text-foreground">Dostava</p>
                  <p className="text-xs text-muted-foreground font-ui mt-1">Kurirska dostava na adresu</p>
                </button>
                <button
                  type="button"
                  onClick={() => setDeliveryType('PICKUP')}
                  className={`rounded-xl border p-4 text-left transition-colors ${
                    deliveryType === 'PICKUP'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <p className="font-ui text-sm font-semibold text-foreground">Lično preuzimanje</p>
                  <p className="text-xs text-muted-foreground font-ui mt-1">Preuzimanje bez troskova dostave</p>
                </button>
              </div>

              {deliveryType === 'DELIVERY' && (
                <>
                  <h2 className="font-serif text-lg font-semibold text-foreground mb-5">Adresa dostave</h2>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="address" className="font-ui text-sm text-foreground mb-1.5 block">Ulica i broj *</Label>
                      <Input id="address" {...register('address')} placeholder="Knez Mihailova 15" className="font-ui" />
                      <FieldError name="address" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city" className="font-ui text-sm text-foreground mb-1.5 block">Grad *</Label>
                        <Input id="city" {...register('city')} placeholder="Beograd" className="font-ui" />
                        <FieldError name="city" />
                      </div>
                      <div>
                        <Label htmlFor="postalCode" className="font-ui text-sm text-foreground mb-1.5 block">Postanski broj *</Label>
                        <Input id="postalCode" {...register('postalCode')} placeholder="11000" className="font-ui" />
                        <FieldError name="postalCode" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="note" className="font-ui text-sm text-foreground mb-1.5 block">Napomena za dostavu</Label>
                      <Textarea
                        id="note"
                        {...register('note')}
                        placeholder="Npr. Pozovite pre isporuke, sprat 2..."
                        className="font-ui resize-none"
                        rows={3}
                      />
                    </div>
                  </div>
                </>
              )}
            </motion.div>

            <div className="flex items-center gap-2 text-sm font-ui text-muted-foreground p-4 bg-muted/50 rounded-xl">
              <ShieldCheck size={16} className="text-primary shrink-0" />
              Plaćanje pouzećem pri preuzimanju porudžbine
            </div>
          </div>

          {/* Order summary */}
          <div>
            <div className="bg-card rounded-xl shadow-card border border-border p-6 sticky top-28">
              <h2 className="font-serif text-lg font-bold text-foreground mb-4">Vaša porudžbina</h2>
              <div className="space-y-3 mb-4">
                {items.map(item => (
                  <div key={item.product.id} className="flex gap-3">
                    <img src={item.product.primaryImageUrl} alt={item.product.name} className="h-12 w-12 object-cover rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-sm font-medium text-foreground line-clamp-1">{item.product.name}</p>
                      <p className="text-xs text-muted-foreground font-ui">
                        {item.quantity} x {item.product.price.toLocaleString('sr-RS')} RSD
                      </p>
                    </div>
                    <span className="font-ui text-sm font-semibold text-foreground shrink-0">
                      {(item.product.price * item.quantity).toLocaleString('sr-RS')}
                    </span>
                  </div>
                ))}
              </div>
              <Separator className="mb-4" />
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm font-ui">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-foreground">{pricing.subtotal.toLocaleString('sr-RS')} RSD</span>
                </div>
                <div className="flex justify-between text-sm font-ui">
                  <span className="text-muted-foreground">Dostava</span>
                  <span className={pricing.shipping === 0 ? 'text-primary font-medium' : 'text-foreground'}>
                    {pricing.shipping === 0 ? 'BESPLATNO' : `${pricing.shipping} RSD`}
                  </span>
                </div>
              </div>
              {deliveryType === 'DELIVERY' && pricing.shipping > 0 && (
                <DeliveryNotice variant="compact" className="mb-4" />
              )}
              <Separator className="mb-4" />
              <div className="flex justify-between font-bold font-ui mb-6">
                <span className="text-foreground">Ukupno</span>
                <span className="text-primary text-xl">{pricing.total.toLocaleString('sr-RS')} RSD</span>
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="w-full bg-primary text-primary-foreground hover:bg-primary-light font-ui shadow-btn"
              >
                {submitting ? (
                  <><Loader2 size={18} className="animate-spin mr-2" /> Obrada...</>
                ) : (
                  'Potvrdite porudzbinu'
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
